import { Globe, Coins, RefreshCw, CreditCard } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ProcessorDetails } from '@/lib/api/processors'
import { cn } from '@/lib/utils'

interface ApmEntry {
    key: string
    enabled?: boolean
    label?: string
    description?: string
    logoUrl?: string
    supportedCountries?: string[]
    presentmentCurrencies?: string[]
    recurring?: boolean
}

function getApmSettings(processor: ProcessorDetails): ApmEntry[] {
    const options = processor.options as Record<string, unknown> | null
    const apmSettings = options?.apmSettings as Record<string, Record<string, unknown>> | undefined
    if (!apmSettings) return []

    return Object.entries(apmSettings).map(([key, value]) => ({
        key,
        enabled: value.enabled as boolean | undefined,
        label: value.label as string | undefined,
        description: value.description as string | undefined,
        logoUrl: value.logoUrl as string | undefined,
        supportedCountries: value.supportedCountries as string[] | undefined,
        presentmentCurrencies: value.presentmentCurrencies as string[] | undefined,
        recurring: value.recurring as boolean | undefined,
    }))
}

interface ProcessorPaymentMethodsCardProps {
    processor: ProcessorDetails
}

export const ProcessorPaymentMethodsCard = ({ processor }: ProcessorPaymentMethodsCardProps) => {
    const allMethods = getApmSettings(processor)
    const enabledMethods = allMethods.filter((m) => m.enabled)

    if (enabledMethods.length === 0) return null

    return (
        <Card className="mb-6">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5 text-muted-foreground" />
                        <CardTitle>Payment Methods</CardTitle>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                        {enabledMethods.length} of {allMethods.length} enabled
                    </Badge>
                </div>
            </CardHeader>
            <CardContent>
                    <div className="grid gap-3">
                        {enabledMethods.map((method) => (
                            <div
                                key={method.key}
                                className="flex items-center gap-4 rounded-lg border bg-card p-3"
                            >
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-white">
                                    {method.logoUrl ? (
                                        <img
                                            src={method.logoUrl}
                                            alt={method.label || method.key}
                                            className="h-6 w-6 object-contain"
                                        />
                                    ) : (
                                        <CreditCard className="h-5 w-5 text-muted-foreground" />
                                    )}
                                </div>

                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">
                                            {method.label || method.key}
                                        </span>
                                        <Badge variant="default" className="text-[10px] px-1.5 py-0">
                                            Enabled
                                        </Badge>
                                    </div>
                                    {method.description && (
                                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                            {method.description}
                                        </p>
                                    )}
                                </div>

                                <div className="hidden sm:flex items-center gap-4 shrink-0 text-xs text-muted-foreground">
                                    {method.supportedCountries && (
                                        <div className="flex items-center gap-1" title="Supported countries">
                                            <Globe className="h-3.5 w-3.5" />
                                            <span className="tabular-nums">{method.supportedCountries.length}</span>
                                        </div>
                                    )}
                                    {method.presentmentCurrencies && (
                                        <div className="flex items-center gap-1" title="Currencies">
                                            <Coins className="h-3.5 w-3.5" />
                                            <span className="tabular-nums">{method.presentmentCurrencies.length}</span>
                                        </div>
                                    )}
                                    <div
                                        className={cn(
                                            'flex items-center gap-1',
                                            method.recurring ? 'text-foreground' : 'text-muted-foreground/50',
                                        )}
                                        title={method.recurring ? 'Recurring supported' : 'No recurring'}
                                    >
                                        <RefreshCw className="h-3.5 w-3.5" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
            </CardContent>
        </Card>
    )
}
